/**
 * The comments on some pages or sessions, as one map (RFC 0339).
 *
 * A comment lives in the comments document its page or session links by
 * `commentsId`, and a comment on something that is not a document (a code
 * file) in the project's own. This follows those links and merges what it
 * finds with the comments the project still holds from before the move, so a
 * reader filters one map with `projectThreads` exactly as it did when every
 * comment was on the project. Live: a new comment arrives as a change to the
 * comments document, and a page's first comment as a change to the page.
 */

import {
  type AnyDocumentId,
  isValidDocumentId,
} from '@automerge/automerge-repo/slim'
import {
  type CommentMap,
  linkedCommentsIds,
  mergeComments,
} from '@mod/core/model'
import { useMemo } from 'react'
import { useDocument, useDocuments } from '../world/repo/useDocument'

export function useCommentMap(
  projectId: string | undefined,
  subjectIds: readonly string[],
): CommentMap {
  const [projectDoc] = useDocument<unknown>(
    projectId as AnyDocumentId | undefined,
  )
  const key = subjectIds.join('\u0000')
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by contents
  const owners = useMemo(
    () => subjectIds.filter((id) => isValidDocumentId(id)) as AnyDocumentId[],
    [key],
  )
  // A code file's comments are on the project's own comments document.
  const onProject = subjectIds.some((id) => id && !isValidDocumentId(id))
  const ownerDocs = useDocuments<unknown>(owners)
  const commentIds = useMemo(() => {
    const ids = new Set<string>()
    for (const doc of ownerDocs.values()) {
      for (const id of linkedCommentsIds(doc)) ids.add(id)
    }
    if (onProject) {
      for (const id of linkedCommentsIds(projectDoc)) ids.add(id)
    }
    return [...ids] as AnyDocumentId[]
  }, [ownerDocs, onProject, projectDoc])
  const commentDocs = useDocuments<unknown>(commentIds)
  return useMemo(
    () => mergeComments(projectDoc, [...commentDocs.values()]),
    [projectDoc, commentDocs],
  )
}
